import { Component, computed, inject, output } from '@angular/core';
import jsPDF from 'jspdf';
import { LucideModule } from '../../../../../shared/modules/lucide.module';
import { AlertaService } from '../../../../../shared/utils/alerta.service';
import { FORMAS_RECEBIMENTO, PedidoSicStore, TIPOS_SOLICITANTE, rotuloDe } from '../pedido-sic.store';

const DATA_LONGA = new Intl.DateTimeFormat('pt-BR', {
  day: '2-digit',
  month: 'long',
  year: 'numeric',
});

const HORA_CURTA = new Intl.DateTimeFormat('pt-BR', { hour: '2-digit', minute: '2-digit' });

const NAO_INFORMADO = 'Não informado';

/** Medidas do comprovante em milímetros. */
const MARGEM = 16;
const ALTURA_FAIXA = 26;
const ALTURA_LINHA = 5.5;
const LARGURA_ROTULO = 46;
const RODAPE = 280;

interface LinhaComprovante {
  rotulo: string;
  valor: string;
}

@Component({
  selector: 'app-etapa-confirmacao-sic',
  templateUrl: './etapa-confirmacao-sic.component.html',
  styleUrls: ['./etapa-confirmacao-sic.component.css'],
  imports: [LucideModule],
})
export class EtapaConfirmacaoSicComponent {
  private readonly alerta = inject(AlertaService);
  protected readonly store = inject(PedidoSicStore);

  /** Pedido para o fluxo recomeçar; quem reinicia o wizard é o componente pai. */
  readonly novoPedido = output<void>();

  /**
   * O 201 do servidor. A etapa só é aberta depois dele, e tudo o que ela mostra — protocolo,
   * datas, prazo, departamento e município — vem daqui, sem valor de reserva escrito na tela.
   */
  readonly resultado = this.store.resultado;

  readonly protocolo = computed(() => this.resultado()?.protocolo ?? '');

  readonly anonimo = computed(() => this.resultado()?.anonimo ?? this.store.anonimo());

  readonly saudacao = computed(() => {
    const nome = this.anonimo() ? '' : this.store.identificacao().nomeCompleto.trim();
    return nome ? `Sr(a). ${nome},` : 'Prezado(a) cidadão(ã),';
  });

  readonly dataAbertura = computed(() => {
    const abertura = this.dataDe(this.resultado()?.dataAbertura);

    return abertura ? `${DATA_LONGA.format(abertura)} às ${HORA_CURTA.format(abertura)}` : '';
  });

  readonly dataPrazo = computed(() => {
    const limite = this.dataDe(this.resultado()?.dataLimite);

    return limite ? DATA_LONGA.format(limite) : '';
  });

  /** `20 dias corridos (até 04 de novembro de 2026)` — número e unidade do ciclo congelado. */
  readonly prazoTexto = computed(() => {
    const resultado = this.resultado();

    if (!resultado) return '';

    return `${resultado.prazoDias} ${resultado.unidadePrazoDescricao.toLowerCase()} (até ${this.dataPrazo()})`;
  });

  readonly departamentoResponsavel = computed(() => this.resultado()?.departamento ?? '');

  /** O município do canal do órgão, devolvido pelo servidor; sem ele, o texto fica neutro. */
  readonly municipio = computed(() => {
    const resultado = this.resultado();

    if (!resultado?.municipio) return '';

    return resultado.uf ? `${resultado.municipio}/${resultado.uf}` : resultado.municipio;
  });

  readonly orgaoTexto = computed(() =>
    this.municipio() ? `do município de ${this.municipio()}` : 'do município'
  );

  /** A data que a API devolve, sem fuso — é a hora local do servidor, igual à do órgão. */
  private dataDe(valor: string | undefined): Date | null {
    if (!valor) return null;

    const data = new Date(valor);

    return Number.isNaN(data.getTime()) ? null : data;
  }

  /** Monta o comprovante com os dados do pedido e entrega o arquivo ao navegador. */
  salvarPdf(): void {
    try {
      const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });

      this.desenharComprovante(doc);
      doc.save(`comprovante-${this.protocolo()}.pdf`);
    } catch (erro) {
      console.error('Erro ao gerar o comprovante:', erro);

      this.alerta.erro({
        titulo: 'Não foi possível gerar o comprovante',
        texto: 'Tente novamente em instantes.',
      });
    }
  }

  private desenharComprovante(doc: jsPDF): void {
    const larguraPagina = doc.internal.pageSize.getWidth();
    const larguraUtil = larguraPagina - MARGEM * 2;

    this.desenharFaixa(doc, larguraPagina);

    let y = ALTURA_FAIXA + 14;

    y = this.desenharTitulo(doc, 'Protocolo de atendimento', y);
    y = this.desenharProtocolo(doc, y);

    y = this.desenharSecao(doc, 'Dados do protocolo', y, [
      { rotulo: 'Situação', valor: 'Recebido' },
      { rotulo: 'Data de abertura', valor: this.dataAbertura() },
      { rotulo: 'Prazo de resposta', valor: this.prazoTexto() },
      { rotulo: 'Departamento responsável', valor: this.departamentoResponsavel() },
      {
        rotulo: 'Modo de abertura',
        valor: this.store.anonimo() ? 'Solicitação anônima' : 'Solicitação identificada',
      },
    ]);

    if (!this.store.anonimo()) {
      const identificacao = this.store.identificacao();

      y = this.desenharSecao(doc, 'Identificação do solicitante', y, [
        { rotulo: 'Nome completo', valor: this.ou(identificacao.nomeCompleto) },
        { rotulo: 'CPF', valor: this.ou(identificacao.cpf) },
        { rotulo: 'E-mail', valor: this.ou(identificacao.email) },
        { rotulo: 'Telefone', valor: this.ou(identificacao.telefone) },
        { rotulo: 'Tipo de solicitante', valor: this.ou(rotuloDe(TIPOS_SOLICITANTE, identificacao.tipoSolicitante)) },
        {
          rotulo: 'Endereço',
          valor: this.ou(
            [identificacao.logradouro, identificacao.numero, identificacao.complemento, identificacao.bairro]
              .map((parte) => parte.trim())
              .filter(Boolean)
              .join(', ')
          ),
        },
        {
          rotulo: 'Cidade / UF',
          valor: this.ou([identificacao.cidade, identificacao.uf].filter(Boolean).join(' / ')),
        },
        { rotulo: 'CEP', valor: this.ou(identificacao.cep) },
      ]);
    }

    const pedido = this.store.dadosPedido();

    y = this.desenharSecao(doc, 'Dados do pedido', y, [
      ...(this.store.anonimo()
        ? []
        : [{ rotulo: 'Forma de recebimento', valor: this.ou(rotuloDe(FORMAS_RECEBIMENTO, pedido.formaRecebimento)) }]),
      { rotulo: 'Departamento destino', valor: this.ou(this.departamentoResponsavel()) },
      { rotulo: 'Assunto', valor: this.ou(pedido.assunto) },
      {
        rotulo: 'Protocolo vinculado',
        valor: pedido.temProtocoloAnterior ? this.ou(pedido.protocoloAnterior) : 'Sem vínculo',
      },
      ...(this.store.anonimo()
        ? []
        : [
            {
              rotulo: 'Avisos por e-mail',
              valor: pedido.receberAvisos ? 'Sim' : 'Não',
            },
          ]),
    ]);

    y = this.desenharTexto(doc, 'Descrição detalhada', this.ou(pedido.descricao), y, larguraUtil);

    const anexos = this.store.anexos();

    y = this.desenharSecao(
      doc,
      `Anexos (${anexos.length})`,
      y,
      anexos.length
        ? anexos.map((anexo, indice) => ({
            rotulo: `Arquivo ${indice + 1}`,
            valor: anexo.nome,
          }))
        : [{ rotulo: 'Arquivos', valor: 'Nenhum arquivo anexado' }]
    );

    this.desenharRodape(doc, larguraUtil);
  }

  private desenharFaixa(doc: jsPDF, larguraPagina: number): void {
    doc.setFillColor(11, 36, 70);
    doc.rect(0, 0, larguraPagina, ALTURA_FAIXA, 'F');

    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(14);
    doc.text('Comprovante de pedido de informação', MARGEM, 12);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.text(
      this.municipio()
        ? `Serviço de Informação ao Cidadão — ${this.municipio()}`
        : 'Serviço de Informação ao Cidadão',
      MARGEM,
      19
    );
  }

  private desenharTitulo(doc: jsPDF, texto: string, y: number): number {
    doc.setTextColor(107, 114, 128);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.text(texto.toUpperCase(), MARGEM, y);

    return y + 8;
  }

  private desenharProtocolo(doc: jsPDF, y: number): number {
    doc.setTextColor(43, 93, 150);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(20);
    doc.text(this.protocolo(), MARGEM, y);

    return y + 12;
  }

  private desenharSecao(
    doc: jsPDF,
    titulo: string,
    inicio: number,
    linhas: readonly LinhaComprovante[]
  ): number {
    let y = this.quebrarPagina(doc, inicio, linhas.length * ALTURA_LINHA + 14);

    y = this.desenharTitulo(doc, titulo, y);

    doc.setFontSize(10);

    for (const linha of linhas) {
      y = this.quebrarPagina(doc, y, ALTURA_LINHA);

      doc.setTextColor(17, 24, 39);
      doc.setFont('helvetica', 'bold');
      doc.text(`${linha.rotulo}:`, MARGEM, y);

      doc.setTextColor(75, 85, 99);
      doc.setFont('helvetica', 'normal');
      doc.text(linha.valor, MARGEM + LARGURA_ROTULO, y);

      y += ALTURA_LINHA;
    }

    return y + 6;
  }

  /** Bloco de texto corrido, quebrado na largura da página. */
  private desenharTexto(
    doc: jsPDF,
    titulo: string,
    texto: string,
    inicio: number,
    larguraUtil: number
  ): number {
    let y = this.desenharTitulo(doc, titulo, this.quebrarPagina(doc, inicio, 20));

    doc.setTextColor(75, 85, 99);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);

    for (const linha of doc.splitTextToSize(texto, larguraUtil) as string[]) {
      y = this.quebrarPagina(doc, y, ALTURA_LINHA);
      doc.text(linha, MARGEM, y);
      y += ALTURA_LINHA;
    }

    return y + 6;
  }

  private desenharRodape(doc: jsPDF, larguraUtil: number): void {
    const aviso = this.store.anonimo()
      ? 'Pedido aberto de forma anônima: guarde este comprovante, o número do protocolo é a única forma de consultar a resposta.'
      : 'Acompanhe o andamento pela aba "Acompanhamento de solicitação" no portal, pela central telefônica ou no atendimento presencial do SIC.';

    doc.setTextColor(107, 114, 128);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);

    const linhas = doc.splitTextToSize(aviso, larguraUtil) as string[];
    let y = RODAPE;

    for (const linha of linhas) {
      doc.text(linha, MARGEM, y);
      y += 4;
    }

    doc.text(`Emitido em ${this.dataAbertura()}.`, MARGEM, y);
  }

  /** Abre uma página nova quando o conteúdo não cabe no espaço restante. */
  private quebrarPagina(doc: jsPDF, y: number, alturaNecessaria: number): number {
    if (y + alturaNecessaria < RODAPE - 12) {
      return y;
    }

    doc.addPage();

    return MARGEM + 8;
  }

  private ou(valor: string): string {
    return valor.trim() || NAO_INFORMADO;
  }
}
